import {
  CarryOutOutlined,
  FileDoneOutlined,
  FilePdfOutlined,
  IdcardOutlined,
  MoneyCollectFilled,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useFlag } from '@unleash/proxy-client-react';
import { useAnalytics } from 'context/AnalyticsContext';
import {
  AccountCircle,
  CalendarToday,
  ChartDataOutline,
  DashboardOutline,
  DashboardProgressOutline,
  FinanceOutline,
  FolderOpenOutline,
  FunnelPlotOutline,
  Group,
  Groups,
  HandShakeOutline,
  LineChartOutline,
  LocalShipping,
  LocationOn,
  MonitorOutline,
  Paid,
  Person,
  QuickReferenceAll,
  ReconcilationOutline,
  SensorOutline,
  SettingsOutline,
  TeamOutline,
  WaterDropOutline,
} from 'icons-new';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { routePermissions, sidebarRoutes } from 'routes/AllRoutes';
import { selectToken } from 'store/auth/selectors';
import testId from 'utils/testId';
import { usePermissions } from '../context/PermissionsContext';

type MenuItemWithCondition = {
  label: React.ReactNode | string;
  key: string;
  icon?: React.ReactNode;
  'data-testid'?: string;
  enabled?: boolean;
  children?: MenuItemWithCondition[];
};

type RoutesKey = keyof typeof sidebarRoutes;

const getEnabledMenuItems = (
  items: MenuItemWithCondition[],
  trackEvent?: (event: string) => void,
) => {
  if (!items) return [];

  const enabledItems = items
    .map((item) => {
      // Separate icon to put it inside Link component
      const { icon, enabled, ...restItems } = { ...item };
      restItems.label = sidebarRoutes[item.key as RoutesKey] ? (
        <Link
          className="sidebar-label"
          onClick={() => {
            if (item.label) {
              trackEvent?.(`${item.label} Clicked`);
            }
          }}
          to={sidebarRoutes[item.key as RoutesKey]}
        >
          {item.icon} <span className="text-one-line">{item.label}</span>
        </Link>
      ) : (
        <span className="sidebar-label text-one-line">
          {item.icon} <span className="text-one-line">{item.label}</span>
        </span>
      );

      // this allows enabling/disabling the entire route group based on permissions. defaults to true unless explicitly disabled
      const isRouteGroupEnabled = enabled === undefined || enabled;

      if (
        Array.isArray(restItems.children) &&
        restItems.children.length > 0 &&
        isRouteGroupEnabled
      ) {
        const enabledChildren = getEnabledMenuItems(
          restItems.children,
          trackEvent,
        ) as MenuItemWithCondition[];

        return {
          ...restItems,
          href: sidebarRoutes[restItems.key as RoutesKey],
          children: enabledChildren,
        };
      }

      // checking this way to handle the case when enabled is `undefined`
      return enabled === false ? null : restItems;
    })
    .filter(Boolean);

  return enabledItems;
};

const useRouteConfig = () => {
  const { permissions, checkPermissions } = usePermissions();
  const token = useSelector(selectToken);
  const isSystemUtilsEnabled = useFlag('SystemUtils');
  const { analytics } = useAnalytics();

  const MENU_CONFIG = useMemo(
    () => [
      {
        label: 'Season Overview',
        key: 'index',
        icon: <DashboardProgressOutline />,
        enabled: checkPermissions(routePermissions['index']),
      },
      {
        label: 'Task Management',
        key: 'taskManagement',
        icon: <CarryOutOutlined />,
        enabled: checkPermissions(routePermissions['taskManagement']),
      },
      {
        label: 'MRV',
        key: 'mrv',
        icon: <DashboardOutline />,
        enabled: checkPermissions(
          [
            ...routePermissions['awdProgress'],
            ...routePermissions['pipeReview'],
            ...routePermissions['pipes'],
          ],
          false,
        ),
        children: [
          {
            label: 'AWD Progress',
            key: 'awdProgress',
            icon: <WaterDropOutline />,
            'data-testid': testId.sidebar['btn-sidebar-dashboard'],
            enabled: checkPermissions(routePermissions['awdProgress']),
          },
          {
            label: 'AWD Pipe Review',
            key: 'pipeReview',
            icon: <QuickReferenceAll />,
            'data-testid': testId.sidebar['btn-sidebar-pipe_review'],
            enabled: checkPermissions(routePermissions['pipeReview']),
          },
          {
            label: 'AWD Maps',
            key: 'pipes',
            icon: <LocationOn />,
            'data-testid': testId.sidebar['btn-sidebar-pipes'],
            enabled: checkPermissions(routePermissions['pipes']),
          },
        ],
      },
      {
        label: 'Group Management',
        key: 'groupManagement',
        icon: <Groups />,
        children: [
          {
            label: 'Farmer Groups',
            key: 'farmerGroup',
            icon: <Group />,
            'data-testid': testId.sidebar['btn-sidebar-farmer-group'],
            enabled: checkPermissions(routePermissions['farmerGroup']),
          },
          {
            label: 'Stakeholders',
            key: 'stakeholders',
            icon: <HandShakeOutline />,
            ...{
              'data-testid': testId.sidebar['btn-sidebar-stakeholders'],
            },
            enabled: checkPermissions(routePermissions['stakeholders']),
          },
          {
            label: 'Farmers',
            key: 'farmers',
            icon: <Person />,
            ...{ 'data-testid': testId.sidebar['btn-sidebar-farmers'] },
            enabled: checkPermissions(routePermissions['farmers']),
          },
        ],
      },

      {
        label: 'Collections',
        key: 'collections',
        icon: <Paid />,
        enabled: checkPermissions(
          [
            ...routePermissions['collectionOverview'],
            ...routePermissions['hotcash'],
            ...routePermissions['collectionInvoices'],
          ],
          false,
        ),
        children: [
          {
            label: 'Overview',
            key: 'collectionOverview',
            icon: <Paid />,
            'data-testid': testId.sidebar['btn-sidebar-collection-overview'],
            enabled: checkPermissions(routePermissions['collectionOverview']),
          },
          {
            label: 'Live Cash',
            key: 'hotcash',
            icon: <SensorOutline />,
            'data-testid': testId.sidebar['btn-sidebar-hotcash'],
            enabled: checkPermissions(routePermissions['hotcash']),
          },
          {
            label: 'Invoice',
            key: 'collectionInvoices',
            icon: <FolderOpenOutline />,
            'data-testid': testId.sidebar['btn-sidebar-collection-invoice'],
            enabled: checkPermissions(routePermissions['collectionInvoices']),
          },
        ],
      },
      {
        label: 'Sales',
        key: 'sales',
        icon: <FunnelPlotOutline />,
        enabled: checkPermissions(
          [
            ...routePermissions['salesLeads'],
            ...routePermissions['salesAgData'],
            ...routePermissions['salesCalendarView'],
            ...routePermissions['salesGroupProgress'],
            ...routePermissions['salesOverview'],
          ],
          false,
        ),
        children: [
          {
            label: 'Leads',
            key: 'salesLeads',
            icon: <MonitorOutline />,
            enabled: checkPermissions(routePermissions['salesLeads']),
          },
          {
            label: 'Overview',
            key: 'salesOverview',
            icon: <LineChartOutline />,
            enabled: checkPermissions(routePermissions['salesOverview']),
          },
          {
            label: 'Deals Calendar',
            key: 'salesCalendarView',
            icon: <CalendarToday />,
            enabled: checkPermissions(routePermissions['salesCalendarView']),
          },
          {
            label: 'Ags',
            key: 'salesAgData',
            icon: <TeamOutline />,
            enabled: checkPermissions(routePermissions['salesAgData']),
          },
          {
            label: 'Group Progress',
            key: 'salesGroupProgress',
            icon: <FinanceOutline height={29} width={29} />,
            enabled: checkPermissions(routePermissions['salesGroupProgress']),
          },
        ],
      },
      {
        label: 'Orders & Delivery',
        key: 'delivery',
        icon: <ReconcilationOutline />,
        enabled: checkPermissions(
          [...routePermissions['deliveryPackages']],
          false,
        ),
        children: [
          {
            label: 'Packages',
            key: 'deliveryPackages',
            icon: <LocalShipping />,
            'data-testid': testId.sidebar['btn-sidebar-delivery-packages'],
          },
        ],
      },
      {
        label: 'Performance',
        key: 'performance',
        icon: <ChartDataOutline />,
        'data-testid': testId.sidebar['btn-sidebar-performance'],
        enabled: checkPermissions(routePermissions['performance']),
      },
      {
        label: 'Users',
        key: 'users',
        icon: <AccountCircle />,
        'data-testid': testId.sidebar['btn-sidebar-users'],
        enabled: checkPermissions(routePermissions['users']),
      },
      {
        label: 'System Utils',
        key: '  ',
        icon: <SettingsOutline />,
        'data-testid': testId.sidebar['btn-sidebar-system-utils'],
        enabled:
          isSystemUtilsEnabled &&
          checkPermissions(
            [
              ...routePermissions['paymentUtils'],
              ...routePermissions['invoiceUtils'],
              ...routePermissions['salesUtils'],
            ],
            false,
          ),
        children: [
          {
            label: 'Payments',
            key: 'paymentUtils',
            icon: <MoneyCollectFilled style={{ fontSize: '20px' }} />,
          },
          {
            label: 'Invoices',
            key: 'invoiceUtils',
            icon: <FilePdfOutlined style={{ fontSize: '20px' }} />,
          },
          {
            label: 'Sales',
            key: 'salesUtils',
            icon: <ShoppingOutlined style={{ fontSize: '20px' }} />,
          },
        ],
      },
      {
        label: 'Admin Review',
        key: '',
        icon: <FileDoneOutlined />,
        enabled: checkPermissions(routePermissions['adminReview']),
        children: [
          {
            label: 'Stakeholder Review',
            key: 'adminReview',
            icon: <IdcardOutlined style={{ fontSize: '20px' }} />,
          },
        ],
      },
    ],
    [token, isSystemUtilsEnabled, permissions],
  );

  const menuItems = useMemo(
    () => getEnabledMenuItems(MENU_CONFIG, analytics?.track),
    [MENU_CONFIG, analytics],
  );

  const getUserAccessibleRedirectLocation = () => {
    const firstEnabledRoute = menuItems?.[0];

    if (firstEnabledRoute?.children?.length) {
      return sidebarRoutes[firstEnabledRoute.children[0].key as RoutesKey];
    } else if (
      firstEnabledRoute?.children?.length === 0 ||
      !firstEnabledRoute?.children
    ) {
      return sidebarRoutes[firstEnabledRoute?.key as RoutesKey];
    }

    return null;
  };

  return {
    menuItems: menuItems as MenuItemWithCondition[],
    getUserAccessibleRedirectLocation,
  };
};

export default useRouteConfig;
